import { NavLink } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  LayoutDashboard,
  Activity,
  Rocket,
  Wallet,
  BookOpen,
  Users,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  LogOut,
  Shield,
  ShieldCheck,
  Settings2,
  CreditCard,
  Package,
  FileText,
  FilePenLine,
  ShoppingCart,
  ReceiptText,
  BarChart3,
  Contact,
  Building2,
  Landmark,
  Briefcase,
  Timer,
  Zap,
  Scale,
  FileBox,
  Inbox,
  Receipt,
  Calculator,
} from 'lucide-react'
import { api } from '@/api/client'
import { useOffline } from '@/hooks/use-offline'
import { SiteAnnouncementBar } from '@/components/layout/SiteAnnouncementBar'
import { ApiStatusBar } from '@/components/layout/ApiStatusBar'
import { StateIndicator } from '@/components/ui/state-indicator'
import { OrgSwitcher } from '@/components/layout/OrgSwitcher'
import { SessionExpiredOverlay } from '@/components/common/SessionExpiredOverlay'
import { UpdateBanner } from '@/components/common/UpdateBanner'
import type { AnnouncementSettings, SystemState, User } from '@/api/types'
import { cn } from '@/lib/utils'
import { isDesktop } from '@/lib/platform'
import { isShowcaseMode, deactivateShowcase } from '@/lib/showcase-mode'
import { DesktopTitleBar } from '@/components/desktop/DesktopTitleBar'
import { useEffect, useState, type ReactNode } from 'react'
import { useLayoutWide } from '@/hooks/use-layout-wide'

interface LayoutProps {
  children: ReactNode
  cancellationBanner?: ReactNode
  ledger: {
    systemState: SystemState
    isReady: boolean
    error: string | null
  }
  user?: User | null
  onLogout?: () => void
  features?: Record<string, boolean> | null
  classicMode?: {
    enabled: boolean
    expired: boolean
    daysRemaining: number | null
    onToggle: (enabled: boolean) => void
  }
}

const NAV_ITEMS = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', requiresReady: true },
  { to: '/readiness', icon: Activity, label: 'Readiness', requiresReady: false, roles: ['site_admin'] as string[] },
  { to: '/provisioning', icon: Rocket, label: 'Provisioning', requiresReady: false, roles: ['site_admin'] as string[] },
  { to: '/accounts', icon: Wallet, label: 'Accounts', requiresReady: true },
  { to: '/journal-entries', icon: BookOpen, label: 'Journal Entries', requiresReady: true },
  { to: '/quotes', icon: FileText, label: 'Quotes', requiresReady: true, feature: 'invoicing' },
  { to: '/invoices', icon: FilePenLine, label: 'Invoices', requiresReady: true, feature: 'invoicing' },
  { to: '/purchase-orders', icon: ShoppingCart, label: 'Purchase Orders', requiresReady: true, feature: 'purchase_orders' },
  { to: '/bills', icon: FileBox, label: 'Bills', requiresReady: true, feature: 'bills' },
  { to: '/in-tray', icon: Inbox, label: 'In-Tray', requiresReady: true, feature: 'supplier_invoice_email' },
  { to: '/credit-notes', icon: ReceiptText, label: 'Credit Notes', requiresReady: true, feature: 'invoicing' },
  { to: '/contacts', icon: Contact, label: 'Contacts', requiresReady: true },
  { to: '/inventory', icon: Package, label: 'Inventory', requiresReady: true, feature: 'inventory' },
  { to: '/fixed-assets', icon: Building2, label: 'Fixed Assets', requiresReady: true, feature: 'fixed_assets' },
  { to: '/banking', icon: Landmark, label: 'Banking', requiresReady: true, feature: 'banking' },
  { to: '/jobs', icon: Briefcase, label: 'Jobs & Tracking', requiresReady: true, feature: 'jobs' },
  { to: '/time-tracking', icon: Timer, label: 'Time Tracking', requiresReady: true, feature: 'timesheets' },
  { to: '/expense-claims', icon: ReceiptText, label: 'Expense Claims', requiresReady: true, feature: 'expense_claims' },
  { to: '/utilities', icon: Zap, label: 'Utilities', requiresReady: true, feature: 'utility_accounts' },
  { to: '/bill-comparison', icon: Scale, label: 'Bill Comparison', requiresReady: true, feature: 'utility_accounts' },
  { to: '/tax', icon: Calculator, label: 'Tax', requiresReady: true, feature: 'tax_v3' },
  { to: '/gst-filing', icon: Receipt, label: 'GST Filing', requiresReady: true, feature: 'gst_filing' },
  { to: '/reports', icon: BarChart3, label: 'Reports', requiresReady: true, feature: 'reports' },
  { to: '/team', icon: Users, label: 'Team', requiresReady: true, roles: ['site_admin', 'owner'] as string[] },
  { to: '/billing', icon: CreditCard, label: 'Billing & Wallet', requiresReady: true },
  { to: '/admin', icon: Settings2, label: 'Administration', requiresReady: true, roles: ['site_admin', 'owner'] as string[] },
  { to: '/site-admin', icon: ShieldCheck, label: 'Site Admin', requiresReady: true, roles: ['site_admin'] as string[] },
]

const STATE_LABELS: Record<SystemState, string> = {
  uninitialised: 'Uninitialised',
  initialising: 'Initialising',
  ready: 'Ready',
  degraded: 'Degraded',
}

export function Layout({ children, cancellationBanner, ledger, user, onLogout, features, classicMode }: LayoutProps) {
  const { readOnly } = useOffline()
  const { wide: layoutWide } = useLayoutWide()
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [announcement, setAnnouncement] = useState<AnnouncementSettings | null>(null)
  const [announcementVisible, setAnnouncementVisible] = useState(false)
  const showcase = isShowcaseMode()

  const handleLogout = () => {
    if (showcase) {
      deactivateShowcase()
      return
    }
    onLogout?.()
  }

  useEffect(() => {
    let active = true

    api.getAnnouncementSettings()
      .then((settings) => {
        if (!active) return

        if (!settings.enabled || !settings.message.trim()) {
          setAnnouncement(null)
          setAnnouncementVisible(false)
          return
        }

        let dismissed = false
        if (settings.dismissible) {
          try {
            dismissed = window.localStorage.getItem(`ledger-announcement-dismissed:${settings.version}`) === 'true'
          } catch {
            dismissed = false
          }
        }

        setAnnouncement(settings)
        setAnnouncementVisible(!dismissed)
      })
      .catch(() => {
        if (!active) return
        setAnnouncement(null)
        setAnnouncementVisible(false)
      })

    return () => {
      active = false
    }
  }, [])

  const dismissAnnouncement = () => {
    if (!announcement?.dismissible) return

    try {
      window.localStorage.setItem(`ledger-announcement-dismissed:${announcement.version}`, 'true')
    } catch {
      // ignore localStorage failures
    }

    setAnnouncementVisible(false)
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background" {...(readOnly ? { 'data-readonly': true } : {})}>
      {isDesktop() && <DesktopTitleBar />}
      <UpdateBanner />
      {announcement && announcementVisible && (
        <SiteAnnouncementBar announcement={announcement} onDismiss={dismissAnnouncement} />
      )}
      {showcase && (
        <div className="flex items-center justify-between gap-2 bg-amber-500/90 px-4 py-1.5 text-xs font-medium text-amber-950">
          <span>You&apos;re exploring Ledger - data shown is fictional.</span>
          <button onClick={deactivateShowcase} className="rounded bg-amber-950/20 px-2 py-0.5 hover:bg-amber-950/30">
            Exit Showcase
          </button>
        </div>
      )}

      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Mobile overlay */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/50 lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
          )}
        </AnimatePresence>

        {/* Sidebar */}
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-50 flex flex-col border-r border-border bg-card transition-all duration-300 lg:static',
            collapsed ? 'w-[68px]' : 'w-64',
            mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          )}
        >
        {/* Brand */}
        <div className="flex h-16 items-center border-b border-border px-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
                <path d="M8 10h8" />
                <path d="M8 14h4" />
              </svg>
            </div>
            {!collapsed && (
              <motion.div
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                className="overflow-hidden whitespace-nowrap"
              >
                <h1 className="text-base tracking-tight"><span className="font-bold">Ledger.</span> <span className="font-light text-muted-foreground text-sm">Core</span></h1>
                <p className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                  Financial Engine
                </p>
              </motion.div>
            )}
          </div>
        </div>

        {/* State Banner (admin-only) */}
        {user && (user.role === 'site_admin' || user.role === 'admin') && (
          <div className={cn('flex items-center gap-2 border-b border-border px-4 py-3', collapsed && 'justify-center')}>
            <StateIndicator state={ledger.systemState} size="sm" />
            {!collapsed && (
              <span className="text-xs font-medium text-muted-foreground">
                {STATE_LABELS[ledger.systemState]}
              </span>
            )}
          </div>
        )}

        {/* Organisation Switcher */}
        {user && <OrgSwitcher collapsed={collapsed} />}

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-4">
          {NAV_ITEMS.filter((item) => {
            // Hide gated items unless the feature is explicitly enabled
            if (item.feature && features?.[item.feature] !== true) return false
            if (!item.roles) return true
            if (!user) return false
            // Platform role check
            if (item.roles.includes(user.role)) return true
            // Org owner/admin can see tenant admin items (but not site-admin-only items)
            const isSiteAdminOnly = item.roles.length === 1 && item.roles[0] === 'site_admin'
            if (!isSiteAdminOnly && user.membership_role && ['owner', 'admin'].includes(user.membership_role)) return true
            return false
          }).map((item) => {
            const disabled = item.requiresReady && !ledger.isReady
            return (
              <NavLink
                key={item.to}
                to={disabled ? '#' : item.to}
                onClick={(e) => {
                  if (disabled) e.preventDefault()
                  setMobileOpen(false)
                }}
                className={({ isActive }) =>
                  cn(
                    'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                    collapsed && 'justify-center px-2',
                    isActive && !disabled
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                    disabled && 'cursor-not-allowed opacity-40',
                  )
                }
              >
                <item.icon className="h-[18px] w-[18px] shrink-0" />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            )
          })}
        </nav>

        {/* User info */}
        {user && (
          <div className={cn('border-t border-border px-3 py-3', collapsed && 'px-2')}>
            <div className={cn('flex items-center gap-3', collapsed && 'justify-center')}>
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                {user.name.charAt(0).toUpperCase()}
              </div>
              {!collapsed && (
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-medium">{user.name}</p>
                  <p className="flex items-center gap-1 text-[10px] text-muted-foreground">
                    <Shield className="h-2.5 w-2.5" />
                    {user.role === 'site_admin'
                      ? 'site admin'
                      : user.memberships?.find((m) => !m.personal)?.role ?? user.memberships?.[0]?.role ?? user.role}
                  </p>
                </div>
              )}
              {!collapsed && (onLogout || showcase) && (
                <button
                  onClick={handleLogout}
                  title="Sign out"
                  className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              )}
            </div>
            {!collapsed && classicMode && (
              <div className="mt-2 flex items-center justify-between rounded-md bg-muted/50 px-2 py-1.5">
                <span className="text-[11px] text-muted-foreground">Command Centre</span>
                <button
                  onClick={() => classicMode.onToggle(!classicMode.enabled)}
                  disabled={classicMode.expired}
                  className={cn(
                    'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors',
                    classicMode.enabled ? 'bg-primary' : 'bg-muted-foreground/30',
                    classicMode.expired && 'cursor-not-allowed opacity-50',
                  )}
                  title={classicMode.expired ? 'Classic mode has expired' : classicMode.daysRemaining != null ? `${classicMode.daysRemaining} days remaining` : undefined}
                >
                  <span className={cn('pointer-events-none inline-block h-4 w-4 rounded-full bg-background shadow transition-transform', classicMode.enabled ? 'translate-x-4' : 'translate-x-0')} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Collapse toggle (desktop only) */}
        <div className="hidden border-t border-border p-2 lg:block">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="flex w-full items-center justify-center rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>

        {/* Mobile close */}
        <div className="border-t border-border p-2 lg:hidden">
          <button
            onClick={() => setMobileOpen(false)}
            className="flex w-full items-center justify-center rounded-lg p-2 text-muted-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        </aside>

        {/* Main content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Top bar */}
          <header className="flex h-16 shrink-0 items-center gap-4 border-b border-border bg-card/80 px-4 backdrop-blur lg:px-8">
            <button
              onClick={() => setMobileOpen(true)}
              className="rounded-lg p-2 text-muted-foreground hover:bg-accent lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>

            <div className="flex-1" />

            <ApiStatusBar />
          </header>

          {/* Page content */}
          <main className="flex-1 overflow-y-auto">
            {cancellationBanner}
            <div className={`mx-auto px-4 py-8 lg:px-8 transition-all duration-300 ${layoutWide ? 'max-w-full' : 'max-w-6xl'}`}>
              {children}
            </div>
          </main>
        </div>
      </div>

      <SessionExpiredOverlay />
    </div>
  )
}
